import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * What a compliance officer decided about one federal exclusion match
 * (PD-13954): a customer account, or a person linked to it, carries the same
 * NPI, or the same name, as a party on the OIG List of Excluded Individuals /
 * Entities (LEIE). Plain varchar, like the other compliance reviews, so a
 * later outcome is an additive change the database sync can carry.
 *
 *   not_same_party — "Not the same party". The officer checked and the
 *                    excluded party is someone else. Hidden until the OIG
 *                    record or our side of the match changes (Chris,
 *                    2026-10-07).
 *   confirmed      — "Confirmed – updating our records". It is the same party;
 *                    the customer changes the account's sales permissions in
 *                    their ERP. Workd keeps NO restriction flag of its own: the
 *                    ERP import and the order-day checks enforce what the ERP
 *                    says. Hidden on the same terms as above.
 *   follow_up      — "Follow up". Hidden until the date the officer picks.
 *   reopened       — the undo of the decision before it, written by Re-open as
 *                    a new row (the table is insert-only). Never hides.
 */
export type ClientExclusionReviewDisposition =
	| 'not_same_party'
	| 'confirmed'
	| 'follow_up'
	| 'reopened';

/** The outcomes an officer records; `reopened` is only written by Re-open. */
export type ClientExclusionReviewDecision = Exclude<
	ClientExclusionReviewDisposition,
	'reopened'
>;

/** Display wording for each disposition — the lists, the export and the modal. */
export const CLIENT_EXCLUSION_REVIEW_DISPOSITION_LABELS: Record<
	ClientExclusionReviewDisposition,
	string
> = {
	not_same_party: 'Not the same party',
	confirmed: 'Confirmed – updating our records',
	follow_up: 'Follow up',
	reopened: 'Re-opened',
};

/**
 * One recorded decision about one federal exclusion match (PD-13954) — the
 * Compliance dashboard's Federal Exclusion card, marked off the way the
 * Adverse Action card is.
 *
 * WHAT A ROW IS ABOUT. A match lives in this tenant's `client_exclusion_match`
 * (one row per subject and LEIE record, written monthly by the licensing
 * screening job). Its identity is `matchIdentity` =
 * `<subject kind>:<subject id>:<LEIE id>` — that table's unique key. Keyed by
 * that rather than by the match row's id, so a decision survives a rebuild of
 * the match table.
 *
 * Modeled on `client_license_duplicate_review` (the Check Your Records
 * mark-off): INSERT-ONLY, the newest non-deleted row per `matchIdentity` is
 * the decision, the note is a column here (it is about the account, not about
 * one license), and who and when are recorded. No foreign key to the client:
 * a decision outlives a merged or deleted account as history.
 *
 * WHAT A DECISION BINDS TO. `matchFingerprint` is the SHA-256 of the OIG
 * record as reviewed (its content hash, reinstatement and waiver dates) and of
 * our side of the match (the account, the key it matched on and the match
 * signals). "Not the same party" and "Confirmed" hide the match only while the
 * live match has that fingerprint, so a new OIG record or a change on our
 * side (an NPI added to the account, say) puts it back in front of the
 * officer. See `federal-exclusion-facts.ts` in crm-api.
 */
@Index('idx_cer_identity', ['matchIdentity', 'reviewedAt'], {})
@Index('idx_cer_client', ['fkClientId', 'reviewedAt'], {})
@Entity('client_exclusion_review')
export class ClientExclusionReviewEntity extends BaseEntity {
	/** `<subject kind>:<subject id>:<LEIE id>`, e.g. `PERSONNEL:1457:5120`. */
	@Column('varchar', { name: 'matchIdentity', length: 64 })
	public matchIdentity!: string;

	/** The account the match is on (`client._id`), as reviewed. */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** `lm_source_leie.id` of the OIG record reviewed. */
	@Column('int', { name: 'leieId', unsigned: true })
	public leieId!: number;

	@Column('varchar', { name: 'disposition', length: 50 })
	public disposition!: ClientExclusionReviewDisposition;

	/** "Follow up" only: the day the match comes back. */
	@Column('date', { name: 'followUpDate', nullable: true })
	public followUpDate!: string | null;

	/** SHA-256 (hex) of the match as reviewed — see the class note. */
	@Column('char', { name: 'matchFingerprint', length: 64 })
	public matchFingerprint!: string;

	/** The mandatory note (optional only on a Re-open). */
	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	/**
	 * NON-BINDING forensics: the match as the officer saw it (the OIG name and
	 * exclusion, who matched and why, the match row id). Nothing here
	 * takes part in whether the decision hides the match.
	 */
	@Column('json', { name: 'reviewedContext', nullable: true })
	public reviewedContext!: Record<string, unknown> | null;

	/** Tenant `user._id` of the officer. */
	@Column('int', {
		name: 'reviewedByUserId',
		unsigned: true,
		nullable: true,
	})
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewedAt' })
	public reviewedAt!: Date;
}
